import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Grid, List, Film, Layers } from "lucide-react";
import PhotoCard from "./PhotoCard";
import PhotoCompareView from "./PhotoCompareView";
import PhotoTransformationTimeline from "./PhotoTransformationTimeline";

interface ProgressPhoto {
  id: string;
  storage_path: string;
  photo_type: string;
  photo_date: string;
  caption: string | null;
  is_visible_to_client: boolean;
}

interface PhotoGalleryProps {
  photos: ProgressPhoto[];
  onDelete?: (photoId: string) => void;
  onToggleVisibility?: (photoId: string, visible: boolean) => void;
  onSelectPhoto?: (photo: ProgressPhoto) => void;
  selectedPhotos?: ProgressPhoto[];
  showActions?: boolean;
  emptyMessage?: string;
}

type GalleryViewMode = "grid" | "list" | "compare" | "timeline";

const PhotoGallery = ({
  photos,
  onDelete,
  onToggleVisibility,
  onSelectPhoto,
  selectedPhotos = [],
  showActions = true,
  emptyMessage = "No photos yet",
}: PhotoGalleryProps) => {
  const [filter, setFilter] = useState<string>("all");
  const [viewMode, setViewMode] = useState<GalleryViewMode>("grid");

  const filteredPhotos = photos.filter((photo) => {
    if (filter === "all") return true;
    return photo.photo_type === filter;
  });

  const photoTypes = [...new Set(photos.map((p) => p.photo_type))];

  if (photos.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center justify-between gap-4">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Photos</SelectItem>
            {photoTypes.map((type) => (
              <SelectItem key={type} value={type}>
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex gap-1">
          <Button
            variant={viewMode === "grid" ? "secondary" : "ghost"}
            size="icon"
            onClick={() => setViewMode("grid")}
            title="Grid view"
          >
            <Grid className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === "list" ? "secondary" : "ghost"}
            size="icon"
            onClick={() => setViewMode("list")}
            title="List view"
          >
            <List className="h-4 w-4" />
          </Button>
          {photos.length >= 2 && (
            <>
              <Button
                variant={viewMode === "compare" ? "secondary" : "ghost"}
                size="icon"
                onClick={() => setViewMode("compare")}
                title="Compare photos"
              >
                <Layers className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === "timeline" ? "secondary" : "ghost"}
                size="icon"
                onClick={() => setViewMode("timeline")}
                title="Timeline view"
              >
                <Film className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Photo Views */}
      {filteredPhotos.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          No photos match the selected filter
        </div>
      ) : viewMode === "compare" ? (
        <PhotoCompareView
          photos={filteredPhotos}
          onClose={() => setViewMode("grid")}
        />
      ) : viewMode === "timeline" ? (
        <PhotoTransformationTimeline
          photos={filteredPhotos}
          photoType={filter !== "all" ? filter : undefined}
          onClose={() => setViewMode("grid")}
        />
      ) : viewMode === "grid" ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredPhotos.map((photo) => (
            <PhotoCard
              key={photo.id}
              photo={photo}
              onDelete={onDelete}
              onToggleVisibility={onToggleVisibility}
              onSelect={onSelectPhoto}
              isSelected={selectedPhotos.some((p) => p.id === photo.id)}
              showActions={showActions}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {filteredPhotos.map((photo) => (
            <PhotoCard
              key={photo.id}
              photo={photo}
              onDelete={onDelete}
              onToggleVisibility={onToggleVisibility}
              onSelect={onSelectPhoto}
              isSelected={selectedPhotos.some((p) => p.id === photo.id)}
              showActions={showActions}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default PhotoGallery;
